import { Tutorial } from '../tutorial.model';

/**
 * Revisão fiscal das categorias, aberta pela etapa de notas do roteiro
 * (`/painel/categorias?onboarding=tributacao`). Não ensina a criar categoria:
 * isso é do guia genérico (`categorias.tour.ts` e `categoria-cadastro.tour.ts`).
 * Os alvos do diálogo são os mesmos `categoria-cadastro.*` dos templates.
 */
export const CATEGORIAS_TRIBUTACAO_TOUR: Tutorial = {
  id: 'categorias-tributacao',
  rota: '/painel/categorias',
  parametros: { onboarding: 'tributacao' },
  passos: [
    {
      alvo: 'categorias.busca',
      titulo: 'Localize a categoria de receita das notas',
      corpo: 'Pesquise pelo nome a categoria vinculada às contas a receber que terão nota. A nota usa os dados fiscais dessa categoria.',
    },
    {
      alvo: 'categorias.filtros',
      titulo: 'Confirme o tipo Contas a receber',
      corpo: 'A tributação de notas fica nas categorias de Contas a receber. Use Filtrar para conferir o tipo e a situação Ativo.',
    },
    {
      alvo: 'categorias.editar',
      titulo: 'Abra a categoria pelo lápis',
      corpo: 'Abra a categoria de receita usada nas notas. Dentro da janela, o guia acompanha a revisão dos dados fiscais.',
    },
  ],
};

export const CATEGORIA_TRIBUTACAO_CADASTRO_TOUR: Tutorial = {
  id: 'categoria-tributacao-cadastro',
  rota: '/painel/categorias',
  parametros: { onboarding: 'tributacao' },
  dialogo: true,
  passos: [
    {
      alvo: 'categoria-cadastro.emissao',
      titulo: 'Confirme Permitir emissão de nota fiscal',
      corpo: 'Confirme Sim para as contas desta categoria emitirem nota. Com Não, as ações de emissão ficam indisponíveis. Ao selecionar Sim, o guia continua nos campos de tributação.',
    },
    {
      alvo: 'categoria-cadastro.tributacao',
      titulo: 'Revise o código de tributação nacional',
      corpo: 'Confira se o código corresponde ao serviço efetivamente prestado. Compare com uma nota já emitida pela empresa ou confirme com a contabilidade antes de salvar.',
    },
    {
      alvo: 'categoria-cadastro.nbs',
      titulo: 'Revise a NBS',
      corpo: 'Confira a NBS do serviço. Ela é obrigatória quando há informações de IBS/CBS. Valide a escolha com a contabilidade ou com uma nota anterior.',
    },
    {
      alvo: 'categoria-cadastro.municipal',
      titulo: 'Revise o código de tributação municipal',
      corpo: 'Confira a combinação de código municipal e nacional exibida para o serviço. Nem todo município adota o código municipal; valide com a contabilidade ou com uma nota emitida.',
    },
    {
      alvo: 'categoria-cadastro.indicador',
      titulo: 'Revise o indicador da operação',
      corpo: 'Este bloco aparece no regime IBS/CBS. Confira o indicador da operação com a contabilidade ou com uma nota anterior da empresa.',
    },
    {
      alvo: 'categoria-cadastro.cst',
      titulo: 'Revise o CST do IBS/CBS',
      corpo: 'Confira o CST. A lista de classificações tributárias mostra apenas as que pertencem a ele. Valide com a contabilidade antes de salvar.',
    },
    {
      alvo: 'categoria-cadastro.classificacao',
      titulo: 'Revise a classificação tributária',
      corpo: 'Confira a classificação tributária. Ao escolhê-la, o CST é ajustado ao dela. Valide a combinação com a contabilidade.',
    },
    {
      alvo: 'categoria-cadastro.credito',
      titulo: 'Revise o crédito presumido',
      corpo: 'Mantenha o crédito presumido somente se a contabilidade indicou. Use Nenhum quando não se aplicar.',
    },
    {
      alvo: 'categoria-cadastro.tributacao-iss',
      titulo: 'Revise a Tributação ISSQN',
      corpo: 'Confira a Tributação ISSQN do serviço. Com Não definido, vale a configuração fiscal da empresa. A escolha vai nas notas desta categoria; confirme com a contabilidade antes de salvar.',
    },
    {
      alvo: 'categoria-cadastro.tipo-imunidade',
      titulo: 'Revise o tipo de imunidade',
      corpo: 'Aparece com Tributação ISSQN Imunidade. Confira o tipo com a contabilidade antes de salvar.',
    },
    {
      alvo: 'categoria-cadastro.comercio-exterior',
      titulo: 'Revise os dados de comércio exterior',
      corpo: 'O grupo Comércio exterior vale para notas de clientes no exterior, de exportação ou com prestação no exterior. Abra o grupo para conferir os campos; ele já vem aberto com Exportação de serviço.',
    },
    {
      alvo: 'categoria-cadastro.modo-prestacao',
      alvoAlternativo: 'categoria-cadastro.comercio-exterior',
      titulo: 'Revise o modo de prestação',
      corpo: 'Confira o Modo de prestação deste serviço. O número no início de cada opção é o código enviado na nota. Valide com a contabilidade.',
    },
    {
      alvo: 'categoria-cadastro.mecanismo-empresa',
      alvoAlternativo: 'categoria-cadastro.comercio-exterior',
      titulo: 'Revise o mecanismo de apoio da sua empresa',
      corpo: 'Confira o Mecanismo de apoio ao comércio exterior da sua empresa. Use 01 - Nenhum quando a empresa não usa programa de apoio neste serviço.',
    },
    {
      alvo: 'categoria-cadastro.mecanismo-cliente',
      alvoAlternativo: 'categoria-cadastro.comercio-exterior',
      titulo: 'Revise o mecanismo de apoio do cliente',
      corpo: 'Confira o Mecanismo de apoio ao comércio exterior do cliente. Use 01 - Nenhum quando o cliente não usa programa de apoio neste serviço.',
    },
    {
      alvo: 'categoria-cadastro.movimentacao-bens',
      alvoAlternativo: 'categoria-cadastro.comercio-exterior',
      titulo: 'Revise a movimentação temporária de bens',
      corpo: 'Confira a Movimentação temporária de bens. Com uma declaração, o número dela é informado na emissão de cada nota.',
    },
    {
      alvo: 'categoria-cadastro.mdic',
      alvoAlternativo: 'categoria-cadastro.comercio-exterior',
      titulo: 'Revise o compartilhamento com o MDIC',
      corpo: 'Confira Compartilhar com o MDIC: define se as notas ficam disponíveis ao Ministério do Desenvolvimento, Indústria, Comércio e Serviços.',
    },
    {
      alvo: 'categoria-cadastro.salvar',
      titulo: 'Salve depois de validar',
      corpo: 'Compare com uma nota já emitida pela empresa ou confirme com a contabilidade antes de salvar. Salvar grava a categoria, mas não emite nota nem comprova a autorização.',
    },
  ],
};
